/** Jobs center, tray, recovery and retention strings (ALP-65/66/67). Owned by the jobs-center track. */
export const jobs = {
  title: "Jobs",
  description: "Every long operation in one place: reloads, syncs, bulk actions, publishing and exports. Live progress, pause, cancel and retry.",
  tabs: { active: "Active", finished: "Finished", failed: "Needs attention", all: "All" },
  categories: { all: "All types", sync: "Sync", bulk: "Bulk actions", ai: "AI", publish: "Publishing", export: "Exports", other: "Other" },
  status: { queued: "Queued", running: "Running", paused: "Paused", succeeded: "Done", failed: "Failed", cancelled: "Cancelled" },
  kinds: {
    resync_post: "Reload comments",
    resync_account: "Sync account",
    backfill_sync: "Import older posts",
    backfill_account: "Initial import",
    reconcile: "Reconciliation",
    bulk_comment_action: "Bulk comment action",
    bulk_flag: "Tag items",
    bulk_archive: "Archive comments",
    publish: "Publish post",
    cross_post: "Cross-post",
    contact_export: "Export a person's data",
    contact_delete: "Delete a person's data",
    enrich_contact: "Fetch commenter profile",
    reembed: "Rebuild search index",
    reclassify: "Re-classify with AI",
    report: "Report",
    semantic_reindex: "Re-index knowledge",
    calendar_refresh: "Refresh calendar",
    automation_run_retry: "Retry automation run",
    account_sync: "Sync account",
    comment_reply: "Comment reply",
    comment_private_reply: "Private reply",
    comment_delete: "Delete comment",
    comment_hide: "Hide comment",
    comment_unhide: "Unhide comment",
    message_send: "Send message",
    bulk_undo: "Undo bulk action",
    usage_reconcile: "Recount plan usage",
    ai_key_provision: "Set up the AI key",
    ai_image: "AI image",
    ai_image_edit: "AI image edit",
    ai_caption: "AI caption",
    ai_score: "Post score",
    ai_rule: "Rule from a prompt",
    ai_tts: "Text to speech",
    ai_stt: "Speech to text",
    ai_occasion: "Post for a calendar day",
    agent_reply: "AI agent reply",
    agent_batch: "AI agent drafts",
    media_refresh: "Refresh media",
    media_repair: "Reload pictures",
    post_reload: "Reload from Instagram",
    people_flag: "Tag people",
    people_mark: "Mark people",
    people_block: "Block in Malfak",
    people_unblock: "Unblock people",
    people_hide_comments: "Hide all their comments",
    people_unhide_comments: "Unhide their comments",
    people_archive_comments: "Archive their comments",
    people_unarchive_comments: "Unarchive their comments",
    people_delete_comments: "Delete all their comments",
    people_export: "Export people",
  },
  counts: { done_one: "{{count}} done", done_other: "{{count}} done", failed_one: "{{count}} failed", failed_other: "{{count}} failed", skipped_one: "{{count}} skipped", skipped_other: "{{count}} skipped", of: "{{done}} of {{total}}", items_one: "{{count}} item", items_other: "{{count}} items" },
  eta: { soon: "Less than a minute left", minutes_one: "About {{count}} min left", minutes_other: "About {{count}} min left", hours_one: "About {{count}} h left", hours_other: "About {{count}} h left", estimating: "Estimating time…", waiting: "Waiting to start…", paused: "Paused — resume any time" },
  actions: {
    cancel: "Cancel",
    pause: "Pause",
    resume: "Resume",
    retry: "Retry",
    retryFailed: "Retry failed items",
    report: "Failure report (CSV)",
    view: "View",
    viewAll: "View all jobs",
    details: "Details",
    open: "Open {{target}}",
  },
  confirmCancel: { title: "Cancel this job?", body: "Items already processed stay done. You can retry the rest later.", confirm: "Cancel job", keep: "Keep running" },
  empty: {
    active: { title: "Nothing running", hint: "Reloads, syncs and bulk actions you start show up here with live progress." },
    finished: { title: "No finished jobs yet", hint: "Completed jobs are kept here with their results." },
    failed: { title: "All clear", hint: "No job failed or left items behind." },
    all: { title: "No jobs yet", hint: "Start a reload on a post or a bulk action in the inbox." },
  },
  drawer: {
    items: "Items",
    failedFirst: "Failed items first",
    noItems: "No per-item results for this job.",
    failedGeneric: "This job failed. Retry it to run it again.",
    noItemsFailed: "This job failed before it recorded per-item results. Retry it to run it again.",
    itemFailedGeneric: "Failed (no reason was recorded)",
    error: "What went wrong",
    result: "Result",
    created: "Created",
    started: "Started",
    finished: "Finished",
    retryOf: "Retry of an earlier job",
    item: "Item {{n}}",
    attempts_one: "{{count}} attempt",
    attempts_other: "{{count}} attempts",
    filterAll: "All",
    filterFailed: "Failed",
    filterOk: "Done",
    filterSkipped: "Skipped",
  },
  tray: {
    label: "Jobs",
    labelActive_one: "Jobs: {{count}} running",
    labelActive_other: "Jobs: {{count}} running",
    title: "Jobs",
    active: "Running now",
    recent: "Recently finished",
    empty: "No jobs running",
    emptyHint: "Long operations appear here with live progress.",
  },
  toast: {
    started: "{{title}} started",
    startedHint: "Follow it in the jobs tray.",
    succeeded: "{{title}} finished",
    failed: "{{title}} failed",
    cancelled: "{{title}} cancelled",
    partial_one: "{{count}} item failed",
    partial_other: "{{count}} items failed",
  },
  ai: {
    ready: {
      image: "Your image is ready",
      edit: "Your edited image is ready",
      text: "Your AI result is ready",
    },
    failedTitle: "{{title}} failed",
    view: "View",
    retry: "Retry",
    useInPost: "Use in post",
    result: "Result",
    noResult: "No result was kept for this job.",
    score: "Score: {{score}}",
    prompt: "Prompt",
  },
  target: { publication: "post", post: "post", account: "account", contact: "person", comments: "comments", comment: "comment", conversation: "conversation", org: "organization" },
  progressLabel: "Progress of {{title}}",
  conflict: "This job can't do that right now.",
  data: {
    retention: {
      title: "Data retention",
      hint: "How long Malfak keeps comments, messages and their history. Deleted-on-Instagram items are kept too, so nothing is lost by accident.",
      forever: "Keep forever",
      foreverHint: "Recommended. Nothing is deleted automatically.",
      days: "Delete after a period",
      daysHint: "Comments and messages older than the period are deleted every night.",
      period: "Keep for",
      periodDays_one: "{{count}} day",
      periodDays_other: "{{count}} days",
      save: "Save retention",
      saved: "Retention updated",
      confirmTitle_one: "Delete data older than {{count}} day?",
      confirmTitle_other: "Delete data older than {{count}} days?",
      confirmBody_one: "Every night, comments, messages and their versions older than {{count}} day are permanently deleted. This can't be undone.",
      confirmBody_other: "Every night, comments, messages and their versions older than {{count}} days are permanently deleted. This can't be undone.",
      confirm: "Turn on deletion",
      readOnly: "Only owners and admins can change retention.",
      current: "Current policy",
      updated: "Updated {{when}}",
    },
    incidents: {
      title: "Incidents",
      hint: "Malfak checks every connected account every few minutes. If it finds comments or messages that live updates missed, it raises an incident.",
      open: "Open",
      resolved: "Resolved",
      all: "All",
      empty: "No incidents",
      emptyHint: "Live updates are arriving normally.",
      resolve: "Mark resolved",
      resolvedToast: "Incident resolved",
      recovered_one: "{{count}} item recovered",
      recovered_other: "{{count}} items recovered",
      silent_one: "No live updates for {{count}} min",
      silent_other: "No live updates for {{count}} min",
      occurrences_one: "Seen {{count}} time",
      occurrences_other: "Seen {{count}} times",
      opened: "Opened {{when}}",
      lastSeen: "Last seen {{when}}",
      resolvedAt: "Resolved {{when}}",
      adminOnly: "Incidents are visible to owners and admins.",
      explain: "Live updates may be down: recovered items still go through your rules, so nothing is missed.",
    },
  },
  sync: {
    health: "Sync health",
    lastWebhook: "Last live update",
    lastReconcile: "Last check",
    cadence: "Checks",
    cadenceActive: "Every 10 min",
    cadenceQuiet: "Hourly",
    recovered: "Recovered",
    never: "Never",
    resync: "Sync now",
    resyncHint: "Re-reads every post, comment and DM (capped and resumable). Missing items run through your rules.",
    incidentTitle: "Live updates may be down for @{{user}}",
    incidentBody_one: "No live update arrived for {{minutes}} min while {{count}} item was found by the background check. Malfak keeps recovering them automatically.",
    incidentBody_other: "No live update arrived for {{minutes}} min while {{count}} items were found by the background check. Malfak keeps recovering them automatically.",
  },
};
